/*
 * Karl's animated portrait. The compact size is the one the narrow task pane
 * uses beside actions and badges. The portraits are decorative: the name, the
 * role and the badge beside them carry the meaning. The loop is a VP9 WebM with
 * alpha, the still is its first frame, and reduced motion never starts a loop.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "../../i18n/useTranslation";
import { KARL_AVATAR_MEDIA, type KarlAvatarState } from "../../karl/karlAvatar";

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function KarlAvatar({
  state = "idle",
  compact = false,
  animated = true,
  className = "",
}: {
  state?: KarlAvatarState;
  compact?: boolean;
  animated?: boolean;
  className?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
  const media = KARL_AVATAR_MEDIA[state];
  const showLoop = animated && !reduceMotion;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event: MediaQueryListEvent) => setReduceMotion(event.matches);
    setReduceMotion(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  return (
    <span
      className={`karl-avatar${compact ? " karl-avatar--compact" : ""}${className ? ` ${className}` : ""}`}
      data-karl-avatar
      data-karl-state={state}
    >
      <span className="karl-avatar__portrait" aria-hidden="true">
        {showLoop ? (
          <video
            key={media.loop}
            className="karl-avatar__media"
            poster={media.still}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
          >
            <source src={media.loop} type="video/webm" />
            <img className="karl-avatar__media" src={media.still} alt="" decoding="async" />
          </video>
        ) : (
          <img className="karl-avatar__media" src={media.still} alt="" decoding="async" />
        )}
      </span>
      {compact ? null : (
        <span className="karl-avatar__copy">
          <strong className="karl-avatar__name">{t.karlName}</strong>
          <span className="karl-avatar__role">{t.karlRole}</span>
        </span>
      )}
    </span>
  );
}
